import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  LenderChip,
  Price,
  type LenderDisplay,
} from '@hero/web-components';
import type { ApplicationMock, OfferMock } from '../mocks';
import {
  formatOfferFeePercentage,
  formatOfferRepaymentDate,
  formatOfferTerm,
} from '../utils/offerDisplay';

export type AcceptOfferDialogProps = {
  /**
   * The offer the carrier is about to accept. `null` keeps the
   * dialog closed — the parent toggles it by setting / clearing
   * this prop, so the close button works without an extra `open`
   * boolean.
   */
  offer: OfferMock | null;
  application: ApplicationMock;
  /** Resolved lender display (logo, name, brand color), if known. */
  partner?: LenderDisplay;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirm dialog before the carrier locks in an offer. The hero
 * block contrasts **what lands in your account today** (advance)
 * vs **what you repay** (your requested amount) so the economics
 * are obvious before they commit.
 */
export function AcceptOfferDialog({
  offer,
  application,
  partner,
  isSubmitting,
  onCancel,
  onConfirm,
}: AcceptOfferDialogProps) {
  const { t } = useTranslation('m-financing');
  const [acknowledged, setAcknowledged] = useState(false);

  if (!offer || !offer.terms) return null;

  const currency = application.invoice.currency;
  const repaymentDateLabel = formatOfferRepaymentDate(offer.terms.repaymentDate);

  return (
    <Dialog
      open={offer !== null}
      onOpenChange={open => {
        if (!open) {
          setAcknowledged(false);
          onCancel();
        }
      }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('offer_card.accept.modal_title')}
          </DialogTitle>
          <DialogDescription>
            {t('offer_card.accept.modal_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between gap-3">
            {partner ? (
              <LenderChip lender={partner} size="sm" />
            ) : (
              <span className="text-sm font-semibold text-gray-900">
                {offer.lenderName}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-medium text-emerald-800">
                {t('offer_card.accept.you_receive_today')}
              </p>
              <Price
                amount={offer.terms.advancedAmount}
                currency={currency}
                size="lg"
                className="mt-1 font-bold text-emerald-700"
              />
              <p className="mt-2 text-xs leading-relaxed text-emerald-800/80">
                {t('offer_card.accept.receive_hint')}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-700">
                {t('offer_card.accept.you_will_repay')}
              </p>
              <Price
                amount={application.requestedAmount}
                currency={currency}
                size="lg"
                className="mt-1 font-bold text-gray-900"
              />
              <p className="mt-2 text-xs leading-relaxed text-gray-600">
                {t('offer_card.accept.repay_hint')}
              </p>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.repayment_date')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {repaymentDateLabel}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.term')}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">
                {formatOfferTerm(t, offer)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">
                {t('offer_card.fields.fee')}
              </dt>
              <dd className="mt-0.5 font-semibold text-gray-900">
                <Price amount={offer.terms.feeAmount} currency={currency} />
                <span className="ms-1 text-xs font-normal text-gray-500">
                  ({formatOfferFeePercentage(offer.terms.feePercentage)})
                </span>
              </dd>
            </div>
            {offer.terms.notes && (
              <div className="col-span-2 rounded-lg bg-gray-50 p-2.5">
                <dt className="text-xs text-gray-500">
                  {t('offer_card.fields.notes')}
                </dt>
                <dd className="mt-0.5 text-xs leading-relaxed text-gray-700">
                  {offer.terms.notes}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Checkbox
            id="accept-offer-acknowledgment"
            checked={acknowledged}
            onCheckedChange={checked => setAcknowledged(checked === true)}
            disabled={isSubmitting}
          />
          <Label
            htmlFor="accept-offer-acknowledgment"
            className="cursor-pointer text-sm leading-relaxed text-gray-700">
            {t('offer_card.accept.acknowledgment_label')}
          </Label>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setAcknowledged(false);
              onCancel();
            }}
            disabled={isSubmitting}>
            {t('offer_card.accept.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || !acknowledged}>
            {isSubmitting
              ? t('offer_card.accept.submitting')
              : t('offer_card.accept.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
